<script setup lang="ts">
import {Head, useForm} from "@inertiajs/vue3";
import Layout from "@/Pages/auth/layouts/AuthLayout.vue";
import TextField from "@/Pages/auth/components/TextField.vue";
import FormCard from "@/Pages/auth/components/FormCard.vue";
import SingleActionCard from "@/Pages/auth/components/SingleActionCard.vue";
import {ref} from "vue";

interface Props {
  status?: string
}

defineOptions({layout: Layout})
defineProps<Props>()
const form = useForm({
    code: '',
    recovery_code: '',
})

const isRecoveryCodeUsed = ref(false)

const toggleRecoveryCode = () => {
    isRecoveryCodeUsed.value = ! isRecoveryCodeUsed.value

    if (isRecoveryCodeUsed.value) {
        form.reset('recovery_code')
    } else {
        form.reset('code')
    }
}

const submit = () => {
    form.post('/two-factor-challenge', {
        onFinish: () => {
            form.reset()
        },
    })
}
</script>

<template>
    <Head title="Two-factor authentication" />
    <form-card
        :status="status"
        :loading="form.processing"
        :error-messages="form.errors.code || form.errors.recovery_code"
        @submit="submit"
    >
        <template #title>
            Two-factor authentication
        </template>
        <div class="text-center">
            <v-icon
                color="grey-darken-2"
                size="36"
            >
                {{ isRecoveryCodeUsed ? 'mdi-key-variant' : 'mdi-cellphone' }}
            </v-icon>
            <div class="text-h6 font-weight-regular mt-1">
                {{ isRecoveryCodeUsed ? 'Recovery code' : 'Authentication code' }}
            </div>
        </div>
        <text-field
            v-if="!isRecoveryCodeUsed"
            v-model="form.code"
            type="text"
            autocomplete="off"
            placeholder="XXXXXX"
            :autofocus="true"
            :required="true"
            :hide-required-asterisk="true"
        />
        <text-field
            v-if="isRecoveryCodeUsed"
            v-model="form.recovery_code"
            type="text"
            autocomplete="off"
            placeholder="XXXXX-XXXXX"
            :autofocus="true"
            :required="true"
            :hide-required-asterisk="true"
        />
        <template #submit>
            Verify
        </template>
        <template #hint>
            <p class="mt-5">
                If you are unable to access your mobile device, enter one of your recovery codes to verify your identity.
            </p>
        </template>
    </form-card>
    <single-action-card v-if="!isRecoveryCodeUsed">
        <div class="text-start font-weight-bold">
            Having problems?
        </div>
        <ul class="text-start mx-4 mt-2">
            <li>
                <a
                    class="cursor-pointer text-decoration-none text-primary"
                    @click="toggleRecoveryCode"
                >Use a recovery code</a>
            </li>
        </ul>
    </single-action-card>
    <single-action-card v-if="isRecoveryCodeUsed">
        <div class="text-start font-weight-bold">
            Don't have a recovery code?
        </div>
        <ul class="text-start mx-4 mt-2">
            <li>
                <a
                    class="cursor-pointer text-decoration-none text-primary"
                    @click="toggleRecoveryCode"
                >Use your authenticator app</a>
            </li>
        </ul>
    </single-action-card>
</template>

<style scoped>

</style>
